/**
 * ⭐⭐ **A FILA DA LEITURA EM LOTE — PRD §70, 27/10/2026.**
 *
 * > *"O mesmo botão, mas com a possibilidade de adicionarmos quantos PDF's quisermos, óbvio que cada PDF será de 'um'
 * > 'imóvel', mas com isso, conseguimos realizar a leitura 'em lote' daquele investigado."* — Richard, 27/10/2026
 *
 * Uma fila por parte investigada. A tela manda **dois PDFs de cada vez**, um pedido por PDF, pela mesma rota de sempre;
 * a leitura volta e fica aqui até ele revisar e lançar, um por vez. ⛔ **O Hub nunca lança sozinho**: esta fila só lê.
 *
 * ⛔⛔ **Mora SÓ na memória desta aba.** Nada vai ao `localStorage`, ao `sessionStorage` nem ao IndexedDB: a leitura
 * traz nome, qualificação e endereço de gente que não consentiu, e não pode ficar no disco do computador depois de
 * fechada a aba. O preço, que ele aceitou no plano: fechar a aba perde as leituras ainda não lançadas (os PDFs
 * continuam com ele) — e a tela avisa antes. ⭐ Por morar fora dos componentes, trocar de parte ou de tópico NÃO perde a
 * fila; sair da sessão, sim (`esvaziarFilas`).
 *
 * ⚠️ As travas que protegem a máquina são do SERVIDOR (`api/src/lib/vagas-de-leitura.ts`: 2 vagas, 100 por hora,
 * 5 minutos por PDF). As daqui são conforto: não mandar o terceiro PDF para ouvir "ocupado".
 *
 * ⭐⭐ **O CAMINHO ÚNICO — PRD §76, 30/10/2026** (*"deixar apenas um único 'caminho'... estou achando muito botões"*).
 * A fila virou o MOTOR, sem tela própria: o PDF entra pela ficha do imóvel ("Tem a matrícula? Anexe aqui"), e anexar já
 * lê (o aviso do que vai e para quem fica à vista antes de escolher o arquivo). Saíram o "Realizar leitura", o "Deixar
 * de lado" e o arquivo guardado para o "Ler no chat".
 */
import { useSyncExternalStore } from 'react';
import { apiInvestigados, LeituraOcupada, type CotaDoCodex, type LeituraDeMatricula, type LeituraPeloCodex } from './api';
import { leituraCombinada } from './leitura-combinada';

/** Até 20 abertos por parte (na fila, lendo, prontos ou com erro); conforme lança ou descarta, abre espaço. */
export const MAXIMO_ABERTOS = 20;
/** O mesmo teto da rota (50 MB desde 28/10/2026, pedido dele). Acima disto o PDF nem sai do computador. */
export const TAMANHO_MAXIMO = 50 * 1024 * 1024;
const AO_MESMO_TEMPO = 2;
/** O servidor responde em até 600 s, e o proxy corta em 630: passado isto, a tela desiste (e o servidor para junto). */
const PRAZO_NA_TELA_MS = 640_000;
/** Queda de rede: mais duas tentativas, com 3 s de intervalo, antes de virar erro. */
const TENTATIVAS_DE_REDE = 2;

export type EstadoDoItem = 'na-fila' | 'lendo' | 'pronto' | 'erro' | 'repetido' | 'lancado';

/** ⭐ PRD §71 e §76: no máximo 5 PDFs por vez (o teto do Jurídico); o resto fica de fora, e ele anexa de novo. */
export const POR_VEZ = 5;
/** A leitura pelo Codex leva de 1 a 10 minutos no servidor; a tela pergunta a cada 2 s e desiste em 12 minutos. */
const PERGUNTAR_A_CADA_MS = 2_000;
const PRAZO_DO_CODEX_MS = 12 * 60_000;

export type ItemDaFila = {
  id: string;
  /** "Arquivo 7": o nome que a linha mostra. Nunca se repete na fila. */
  ordem: number;
  /** ⚠️ O nome original do arquivo: só na tela, e escondido até ele pedir. Nunca vai ao servidor. */
  nome: string;
  estado: EstadoDoItem;
  leitura: LeituraDeMatricula | null;
  erro: string | null;
  /** O Hub não achou o documento nem o nome, e ele confirmou, com um clique, que o imóvel é desta parte. */
  confirmado: boolean;
  /** O mesmo arquivo já estava na fila: o número dele. */
  repeteDe: number | null;
  /** Quando a leitura começou (para o "lendo há 20 s"). */
  comecouEm: number | null;
  /** ⭐ §71: cerca de quantas páginas o PDF tem (contadas no computador, para a linha do que vai sair). */
  paginas: number | null;
  /** ⭐ §71: a leitura vai pelo Codex (o Hub confere), e em que pé está ("com o GPT"). */
  peloCodex: boolean;
  comOGpt: boolean;
};

type Interno = ItemDaFila & {
  investigadoId: string;
  arquivo: File | null;
  impressao: string;
  esperarAte: number;
  tentativas: number;
  controle: AbortController | null;
  chegada: number;
  /** ⭐ §71: o número do pedido ao Codex, para "Cancelar" tirá-lo também do servidor. */
  pedidoDoCodex: string | null;
  /** ⭐ §77: a leitura que falhou já tentou de novo, sozinha, uma vez. */
  retentou: boolean;
};

const filas = new Map<string, Interno[]>();
const retratos = new Map<string, ItemDaFila[]>();
const contadores = new Map<string, number>();
const ouvintes = new Set<() => void>();
const VAZIA: ItemDaFila[] = [];
let chegadas = 0;
let despertador: ReturnType<typeof setTimeout> | null = null;

const ABERTOS: EstadoDoItem[] = ['na-fila', 'lendo', 'pronto', 'erro'];
/** O que se perde ao fechar a aba: ainda não foi lançado nem descartado. */
const PENDENTES: EstadoDoItem[] = ['na-fila', 'lendo', 'pronto'];

function publico(i: Interno): ItemDaFila {
  return {
    id: i.id,
    ordem: i.ordem,
    nome: i.nome,
    estado: i.estado,
    leitura: i.leitura,
    erro: i.erro,
    confirmado: i.confirmado,
    repeteDe: i.repeteDe,
    comecouEm: i.comecouEm,
    paginas: i.paginas,
    peloCodex: i.peloCodex,
    comOGpt: i.comOGpt,
  };
}

function mudou(investigadoId: string): void {
  retratos.set(investigadoId, (filas.get(investigadoId) ?? []).map(publico));
  cuidarDaSaida();
  for (const o of ouvintes) o();
}

function todos(): Interno[] {
  return [...filas.values()].flat();
}

/* ── O aviso de sair e o título da aba ─────────────────────────────────── */

const segurarSaida = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  /* Navegador antigo pede o texto; o de hoje mostra o dele. */
  e.returnValue = '';
};
let segurando = false;
const PREFIXO = /^\(\d+ prontos?\) /;

function cuidarDaSaida(): void {
  const lista = todos();
  const pendentes = lista.some((i) => PENDENTES.includes(i.estado));
  if (pendentes && !segurando) window.addEventListener('beforeunload', segurarSaida);
  if (!pendentes && segurando) window.removeEventListener('beforeunload', segurarSaida);
  segurando = pendentes;
  /* ⭐ O título diz quantos estão prontos, para ele saber de longe que pode revisar. ⛔ Sem nome nenhum. */
  const prontos = lista.filter((i) => i.estado === 'pronto').length;
  const base = document.title.replace(PREFIXO, '');
  document.title = prontos > 0 ? `(${prontos} ${prontos === 1 ? 'pronto' : 'prontos'}) ${base}` : base;
}

/* ── A vez de cada PDF ─────────────────────────────────────────────────── */

function proximaRodada(): void {
  if (despertador) clearTimeout(despertador);
  despertador = null;
  const agora = Date.now();
  const lista = todos();
  let lendo = lista.filter((i) => i.estado === 'lendo').length;
  /* ⭐ §71: pelo Codex, UM de cada vez (o servidor leva um por vez ao GPT; o segundo leria só no Hub). */
  let lendoPeloCodex = lista.filter((i) => i.estado === 'lendo' && i.peloCodex).length;
  const esperando = lista.filter((i) => i.estado === 'na-fila').sort((a, b) => a.chegada - b.chegada);
  for (const item of esperando) {
    if (lendo >= AO_MESMO_TEMPO) break;
    if (item.esperarAte > agora) continue;
    if (item.peloCodex && lendoPeloCodex >= 1) continue;
    lendo += 1;
    if (item.peloCodex) lendoPeloCodex += 1;
    void ler(item);
  }
  const proximo = esperando.filter((i) => i.estado === 'na-fila' && i.esperarAte > agora).map((i) => i.esperarAte);
  if (proximo.length) despertador = setTimeout(proximaRodada, Math.max(250, Math.min(...proximo) - agora));
}

async function ler(item: Interno): Promise<void> {
  if (!item.arquivo) return;
  const controle = new AbortController();
  const prazo = setTimeout(() => controle.abort('prazo'), PRAZO_NA_TELA_MS);
  item.controle = controle;
  item.estado = 'lendo';
  item.comecouEm = Date.now();
  item.erro = null;
  mudou(item.investigadoId);
  try {
    item.leitura = item.peloCodex
      ? await lerPeloCodex(item, controle.signal)
      : await apiInvestigados.lerMatricula(item.investigadoId, item.arquivo, controle.signal);
    item.estado = 'pronto';
  } catch (e) {
    if (controle.signal.aborted && controle.signal.reason === 'tirado') return;
    if (e instanceof LeituraOcupada) {
      /* O servidor está com as duas vagas tomadas (outra aba, a Ester): espera e tenta de novo. Não é erro. */
      item.estado = 'na-fila';
      item.esperarAte = Date.now() + Math.max(1, e.esperar) * 1000;
    } else if (!item.retentou && podeTentarDeNovo(e, controle.signal.aborted)) {
      /* ⭐ §77: a primeira falha tenta de novo sozinha, uma vez (sem botão); a segunda vira a ficha em branco com o motivo. */
      item.retentou = true;
      item.estado = 'na-fila';
      item.esperarAte = Date.now() + 3000;
    } else if (controle.signal.aborted) {
      item.estado = 'erro';
      item.erro = 'A leitura demorou demais e foi interrompida.';
    } else if (e instanceof TypeError && item.tentativas < TENTATIVAS_DE_REDE) {
      /* Queda da conexão no meio: volta para a fila. */
      item.tentativas += 1;
      item.estado = 'na-fila';
      item.esperarAte = Date.now() + 3000;
    } else {
      item.estado = 'erro';
      item.erro = e instanceof TypeError ? 'Sem conexão com o Hub. Tente de novo.' : e instanceof Error ? e.message : 'Não consegui ler a matrícula.';
    }
  } finally {
    clearTimeout(prazo);
    item.controle = null;
    item.comecouEm = item.estado === 'lendo' ? item.comecouEm : null;
    if (filas.get(item.investigadoId)?.includes(item)) mudou(item.investigadoId);
    proximaRodada();
  }
}

/** ⭐ §77: o que vale tentar de novo (o tempo, a rede, o servidor); o arquivo errado e o CPF que falta, não. */
function podeTentarDeNovo(e: unknown, porTempo: boolean): boolean {
  if (porTempo) return true;
  const msg = e instanceof Error ? e.message : '';
  return !/CPF ou CNPJ|n[aã]o [eé] um PDF|50 MB|investigado n[aã]o encontrado|desligada/i.test(msg);
}

/**
 * ⭐⭐ PRD §71 (M5): a leitura pelo Codex. O Hub lê e manda ao Codex no mesmo pedido; a tela pergunta pelo resultado e
 * junta as duas (`leituraCombinada`): a proposta do Codex, conferida pelo fiscal, no mesmo resumo e no mesmo formulário.
 * Sem o Codex (a cota, o login, o caso que não vai a IA de fora), vale a leitura do Hub, dizendo por quê.
 */
async function lerPeloCodex(item: Interno, sinal: AbortSignal): Promise<LeituraDeMatricula> {
  const r = await apiInvestigados.lerMatriculaPeloCodex(item.investigadoId, item.arquivo!, sinal);
  if (!r.codex) return { ...r.leitura, pelo: 'hub', soNoHub: r.semCodex ?? 'A leitura pelo Codex não saiu.' };
  item.pedidoDoCodex = r.codex.id;
  item.comOGpt = true;
  mudou(item.investigadoId);
  const fim = Date.now() + PRAZO_DO_CODEX_MS;
  let ultima: LeituraPeloCodex | null = null;
  while (Date.now() < fim) {
    if (sinal.aborted) throw new DOMException('desistiu', 'AbortError');
    await new Promise((x) => setTimeout(x, PERGUNTAR_A_CADA_MS));
    ultima = await apiInvestigados.leituraPeloCodex(item.investigadoId, r.codex.id, sinal);
    if (ultima.situacao === 'pronto' || ultima.situacao === 'falhou' || ultima.situacao === 'cancelado') break;
  }
  item.comOGpt = false;
  return leituraCombinada(r.leitura, ultima);
}

/** Cerca de quantas páginas o PDF tem (os objetos de página; o PDF de objetos comprimidos fica sem número). */
async function contarPaginas(arquivo: File): Promise<number | null> {
  try {
    const texto = new TextDecoder('latin1').decode(await arquivo.arrayBuffer());
    const n = (texto.match(/\/Type\s*\/Page(?![a-zA-Z])/g) ?? []).length;
    return n > 0 ? n : null;
  } catch {
    return null;
  }
}

/* ── ⭐ §71: a leitura pelo Codex ligada? (a tela pergunta ao servidor; sem resposta, vale o de sempre) ── */
let codex: CotaDoCodex | null = null;
const ouvintesDaCota = new Set<() => void>();
export async function atualizarCota(investigadoId: string): Promise<CotaDoCodex | null> {
  try {
    codex = await apiInvestigados.cotaDoCodex(investigadoId);
  } catch {
    codex = null;
  }
  for (const o of ouvintesDaCota) o();
  return codex;
}
export function usarCota(): CotaDoCodex | null {
  return useSyncExternalStore(
    (o) => {
      ouvintesDaCota.add(o);
      return () => ouvintesDaCota.delete(o);
    },
    () => codex,
  );
}

/* ── O que a tela pede ─────────────────────────────────────────────────── */

async function impressaoDe(arquivo: File): Promise<string> {
  try {
    const resumo = await crypto.subtle.digest('SHA-256', await arquivo.arrayBuffer());
    return [...new Uint8Array(resumo)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    /* Sem a conta segura do navegador: o nome, o tamanho e a data dizem quase o mesmo. */
    return `${arquivo.name}|${arquivo.size}|${arquivo.lastModified}`;
  }
}

/**
 * Põe os PDFs na fila da parte, e a leitura começa (⭐ §76: anexar já lê; com a leitura pelo Codex ligada, vai pelo
 * Codex, e o servidor decide se o caso só lê no Hub). Até 5 por vez: o resto volta em `foraPorVez`. Devolve também
 * quantos ficaram de fora pelo teto de 20 abertos.
 * ⚠️ O que não é PDF ou passa de 50 MB entra como erro (para ele ver qual foi), sem sair do computador.
 */
export async function acrescentar(
  investigadoId: string,
  todos: File[],
): Promise<{ aceitos: number; deFora: number; foraPorVez: number; repetidos: number[] }> {
  const ligada = (await atualizarCota(investigadoId))?.ligada === true;
  const arquivos = todos.slice(0, POR_VEZ);
  const foraPorVez = todos.length - arquivos.length;
  const lista = filas.get(investigadoId) ?? [];
  filas.set(investigadoId, lista);
  let aceitos = 0;
  let deFora = 0;
  /* ⭐ §77: o mesmo arquivo de novo não entra (antes virava uma linha "repetido"); volta o número do primeiro */
  const repetidos: number[] = [];
  for (const arquivo of arquivos) {
    if (lista.filter((i) => ABERTOS.includes(i.estado)).length >= MAXIMO_ABERTOS) {
      deFora += 1;
      continue;
    }
    const impressao = await impressaoDe(arquivo);
    const igual = lista.find((i) => i.impressao === impressao && i.estado !== 'lancado');
    if (igual) {
      repetidos.push(igual.ordem);
      continue;
    }
    const ordem = (contadores.get(investigadoId) ?? 0) + 1;
    contadores.set(investigadoId, ordem);
    const ehPdf = arquivo.type === 'application/pdf' || /\.pdf$/i.test(arquivo.name);
    const grande = arquivo.size > TAMANHO_MAXIMO;
    const erro = !ehPdf
      ? 'Não é um PDF.'
      : grande
        ? `Passa de 50 MB (${(arquivo.size / 1024 / 1024).toFixed(1).replace('.', ',')} MB): o Hub não lê.`
        : null;
    lista.push({
      id: `${investigadoId}:${ordem}`,
      ordem,
      nome: arquivo.name,
      estado: erro ? 'erro' : 'na-fila',
      leitura: null,
      erro,
      confirmado: false,
      repeteDe: null,
      comecouEm: null,
      paginas: erro ? null : await contarPaginas(arquivo),
      peloCodex: ligada,
      comOGpt: false,
      pedidoDoCodex: null,
      retentou: false,
      investigadoId,
      /* O que não dá para ler não guarda o arquivo. */
      arquivo: erro ? null : arquivo,
      impressao,
      esperarAte: 0,
      tentativas: 0,
      controle: null,
      chegada: ++chegadas,
    });
    aceitos += 1;
  }
  mudou(investigadoId);
  proximaRodada();
  return { aceitos, deFora, foraPorVez, repetidos };
}

function achar(investigadoId: string, id: string): Interno | undefined {
  return filas.get(investigadoId)?.find((i) => i.id === id);
}

function trocar(investigadoId: string, id: string, fazer: (i: Interno) => void): void {
  const item = achar(investigadoId, id);
  if (!item) return;
  fazer(item);
  mudou(investigadoId);
  proximaRodada();
}

/** Tira o PDF da fila ("Descartar esta leitura"). Se estiver lendo, a tela desiste e o servidor para a leitura. */
export function tirar(investigadoId: string, id: string): void {
  const lista = filas.get(investigadoId);
  const item = lista?.find((i) => i.id === id);
  if (!lista || !item) return;
  item.controle?.abort('tirado');
  /* ⭐ §71: "Cancelar" tira também do servidor a leitura pelo Codex (as imagens saem da memória dele). */
  if (item.pedidoDoCodex) void apiInvestigados.cancelarLeituraPeloCodex(investigadoId, item.pedidoDoCodex);
  lista.splice(lista.indexOf(item), 1);
  mudou(investigadoId);
  proximaRodada();
}

/** ⭐ §77: a caixinha "Confirmo que este imóvel é desta parte", que se marca e se desmarca. */
export const confirmarDaParte = (investigadoId: string, id: string, sim = true) =>
  trocar(investigadoId, id, (i) => {
    i.confirmado = sim;
  });

export const tentarDeNovo = (investigadoId: string, id: string) =>
  trocar(investigadoId, id, (i) => {
    if (i.estado !== 'erro' || !i.arquivo) return;
    i.estado = 'na-fila';
    i.erro = null;
    i.tentativas = 0;
    i.esperarAte = 0;
  });

/** ⭐ PRD §75: o número do pedido ao Codex de uma linha da fila (para contar os campos corrigidos no lançamento). */
export const pedidoDoCodexDaFila = (investigadoId: string, id: string): string | null =>
  filas.get(investigadoId)?.find((i) => i.id === id)?.pedidoDoCodex ?? null;

/** Lançado pela ficha: o item fica como lançado (para a conta e para o "mesmo imóvel"), e o arquivo sai da memória. */
export const marcarLancado = (investigadoId: string, id: string) =>
  trocar(investigadoId, id, (i) => {
    i.estado = 'lancado';
    i.arquivo = null;
  });

/** ⛔ Ao sair da sessão (ou ela expirar): nenhuma leitura fica na memória da aba. */
export function esvaziarFilas(): void {
  for (const i of todos()) i.controle?.abort('tirado');
  const partes = [...filas.keys()];
  filas.clear();
  contadores.clear();
  for (const p of partes) mudou(p);
  cuidarDaSaida();
}

function assinar(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/** ⭐ §76: a fila de uma parte agora, fora de um componente (o "Lançar" que decide a próxima). */
export function filaDe(investigadoId: string): ItemDaFila[] {
  return retratos.get(investigadoId) ?? VAZIA;
}

/** A fila de uma parte, viva: muda quando um PDF muda de estado. */
export function usarFila(investigadoId: string): ItemDaFila[] {
  return useSyncExternalStore(assinar, () => retratos.get(investigadoId) ?? VAZIA);
}
